import {useMutation, gql} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {useState} from 'react';
import {
  ScheduleInvitation,
  ScheduleInvitationVariables,
} from '../../../schema-types/ScheduleInvitation';
import {
  SendQuestionnaires,
  SendQuestionnairesVariables,
} from '../../../schema-types/SendQuestionnaires';
import {REVIEW_INVITATION_SUMMARY_FRAGMENT} from '../../../graphql/fragments';
import validateUUID from '../../../utils/validateUUID';

const SCHEDULE_INVITATION = gql`
  mutation ScheduleInvitation($reviewId: UUID!, $invitationOwnerId: UUID) {
    scheduleInvitation(reviewId: $reviewId, invitationOwnerId: $invitationOwnerId) {
      ok
      review {
        ...ReviewInvitationSummaryFragment
      }
      errors
    }
  }
  ${REVIEW_INVITATION_SUMMARY_FRAGMENT}
`;

const SEND_QUESTIONNAIRES = gql`
  mutation SendQuestionnaires(
    $reviewId: UUID!
    $isEcqRequired: Boolean
    $isRtqRequired: Boolean
    $overrideRequiredChecks: Boolean
  ) {
    sendQuestionnaires(
      reviewId: $reviewId
      isEcqRequired: $isEcqRequired
      isRtqRequired: $isRtqRequired
      overrideRequiredChecks: $overrideRequiredChecks
    ) {
      ok
      review {
        ...ReviewInvitationSummaryFragment
      }
      errors
    }
  }
  ${REVIEW_INVITATION_SUMMARY_FRAGMENT}
`;

export interface UseInvitationActionsOptions {
  accountName?: string;
  onScheduleSuccess?: () => void;
  onScheduleError?: (error: string) => void;
  onQuestionnaireSuccess?: () => void;
  onQuestionnaireError?: (error: string) => void;
}

export interface MutationState {
  success: boolean | null;
  error: boolean | null;
}

export const useInvitationActions = (options: UseInvitationActionsOptions = {}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {
    accountName,
    onScheduleSuccess,
    onScheduleError,
    onQuestionnaireSuccess,
    onQuestionnaireError,
  } = options;

  const [scheduleState, setScheduleState] = useState<MutationState>({
    success: null,
    error: null,
  });
  const [questionnaireState, setQuestionnaireState] = useState<MutationState>({
    success: null,
    error: null,
  });

  const [scheduleInvitationMutation, {loading: isScheduling}] = useMutation<
    ScheduleInvitation,
    ScheduleInvitationVariables
  >(SCHEDULE_INVITATION, {
    onCompleted: (mutationData) => {
      if (
        mutationData.scheduleInvitation?.ok &&
        !mutationData.scheduleInvitation.errors?.length
      ) {
        setScheduleState({success: true, error: false});
        const successMessage = accountName
          ? `Invitation sent successfully for ${accountName}`
          : 'Invitation sent successfully';
        enqueueSnackbar(successMessage, {variant: 'success'});
        onScheduleSuccess?.();
      } else {
        setScheduleState({success: false, error: true});
        const errorMessage = accountName
          ? `Error sending invitation for ${accountName}: ${
              mutationData.scheduleInvitation?.errors?.join(', ') || 'Unknown error'
            }`
          : `Error sending invitation: ${
              mutationData.scheduleInvitation?.errors?.join(', ') || 'Unknown error'
            }`;
        enqueueSnackbar(errorMessage, {variant: 'error', persist: true});
        onScheduleError?.(errorMessage);
      }
    },
    onError: (mutationError) => {
      setScheduleState({success: false, error: true});
      const errorMessage = accountName
        ? `Error sending invitation for ${accountName}: ${mutationError.message}`
        : `Error sending invitation: ${mutationError.message}`;
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        persist: true,
      });
      onScheduleError?.(errorMessage);
    },
  });

  const [sendQuestionnairesMutation, {loading: isSendingQuestionnaires}] = useMutation<
    SendQuestionnaires,
    SendQuestionnairesVariables
  >(SEND_QUESTIONNAIRES, {
    onCompleted: (mutationData) => {
      if (
        mutationData.sendQuestionnaires?.ok &&
        !mutationData.sendQuestionnaires.errors?.length
      ) {
        setQuestionnaireState({success: true, error: false});
        const successMessage = accountName
          ? `Questionnaires sent successfully for ${accountName}`
          : 'Questionnaires sent successfully';
        enqueueSnackbar(successMessage, {variant: 'success'});
        onQuestionnaireSuccess?.();
      } else {
        setQuestionnaireState({success: false, error: true});
        const errorMessage = accountName
          ? `Error sending questionnaires for ${accountName}: ${
              mutationData.sendQuestionnaires?.errors?.join(', ') || 'Unknown error'
            }`
          : `Error sending questionnaires: ${
              mutationData.sendQuestionnaires?.errors?.join(', ') || 'Unknown error'
            }`;
        enqueueSnackbar(errorMessage, {variant: 'error', persist: true});
        onQuestionnaireError?.(errorMessage);
      }
    },
    onError: (mutationError) => {
      setQuestionnaireState({success: false, error: true});
      const errorMessage = accountName
        ? `Error sending questionnaires for ${accountName}: ${mutationError.message}`
        : `Error sending questionnaires: ${mutationError.message}`;
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        persist: true,
      });
      onQuestionnaireError?.(errorMessage);
    },
  });

  const scheduleInvitation = async (
    reviewId: string,
    invitationOwnerId?: string
  ): Promise<void> => {
    // Reset state before starting
    setScheduleState({success: null, error: null});
    try {
      await scheduleInvitationMutation({
        variables: {
          reviewId: validateUUID(reviewId)!,
          invitationOwnerId,
        },
      });
    } catch (err) {
      console.error('Error sending invitation:', err);
    }
  };

  const sendQuestionnaires = async (mutationOptions: any): Promise<void> => {
    // Reset state before starting
    setQuestionnaireState({success: null, error: null});
    try {
      await sendQuestionnairesMutation(mutationOptions);
    } catch (err) {
      console.error('Error sending questionnaires:', err);
    }
  };

  return {
    scheduleInvitation,
    sendQuestionnaires,
    isScheduling,
    isSendingQuestionnaires,
    scheduleState,
    questionnaireState,
  };
};
